import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { Dispatch, FC, SetStateAction, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Address, PaginatedResponse, PropertyKind } from "shared/types";
import { getPropertyNameWithShortAddress } from "shared/addressUtils";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Modal
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { useInView } from "react-intersection-observer";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

type Props = {
  opened: boolean;
  onClose: () => void;
  propertiesMeta: UseInfiniteQueryResult<
    InfiniteData<
      PaginatedResponse<{
        kind: PropertyKind | null;
        address: Address;
        id: number;
        magic_link: string | null;
        name: string;
        total_applications: number;
        total_units: number;
      }>,
      unknown
    >,
    Error
  >;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
};

export const AddUnitModal: FC<Props> = ({
  opened,
  onClose,
  propertiesMeta,
  searchTerm,
  setSearchTerm
}) => {
  const { unit, Property, property } = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const { type } = useAuthContext();
  const { ref, inView } = useInView();

  useEffect(() => {
    if (
      inView &&
      propertiesMeta.data?.pages.at(-1)?.has_more &&
      !propertiesMeta.isFetching
    )
      propertiesMeta.fetchNextPage();
  }, [inView, propertiesMeta.isFetching]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-[473px] flex flex-col border border-alpha-100 shadow-xl"
    >
      <div className="flex items-center justify-between border-b border-alpha-100 px-6 py-4">
        <p className="text-xl font-bold">
          Add {unit} to {property}
        </p>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.BASE}
          onClick={onClose}
          HugeIcon={Cancel01Icon}
        />
      </div>

      <div className="px-6">
        {!type.isPMS ? (
          <>
            <button
              className="mb-4 mt-6 w-full rounded-lg border border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50 hover:shadow-none"
              onClick={() => navigate("/properties/add")}
            >
              <div className="flex flex-col items-center py-5 hover:border-none">
                <p className="text-sm font-medium text-primary-500">
                  Create New {Property}
                </p>
              </div>
            </button>
            <div className="flex items-center">
              <div className="w-full border-t border-alpha-80" />
              <p className="whitespace-nowrap px-2.5 text-sm font-normal text-content-300">
                or choose existing {property}
              </p>
              <div className="w-full border-t border-alpha-80" />
            </div>
          </>
        ) : null}
        <Input
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          id="search"
          className="mb-6 mt-4"
        />
      </div>
      <div className="max-h-[432px] overflow-y-auto bg-alpha-40 px-6 py-2 border-t border-alpha-40">
        {propertiesMeta.data?.pages.length &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- something unique is here
        propertiesMeta.data.pages.some((p) => p?.items?.length) ? (
          <>
            {propertiesMeta.data.pages.map((page, page_idx) =>
              page.items.map((property) => {
                if (property.kind === PropertyKind.SingleFamily) return null;

                return (
                  <button
                    key={property.id + " " + page_idx}
                    className="mb-2 w-full animate-appear rounded-lg border border-alpha-100 bg-background-1000 hover:bg-alpha-40 hover:shadow-none"
                    onClick={() =>
                      navigate(`/properties/${property.id}/units/add`, {
                        state: {
                          property
                        }
                      })
                    }
                  >
                    <div className="px-6 py-4">
                      <p className="text-left text-base font-medium">
                        {property.name}
                      </p>
                      <p className="text-left text-sm font-medium text-content-200">
                        {getPropertyNameWithShortAddress(
                          property,
                          property.address
                        )}
                      </p>
                    </div>
                  </button>
                );
              })
            )}

            {propertiesMeta.data.pages.at(-1)?.has_more ? (
              <div ref={ref}>
                <Loader />
              </div>
            ) : null}
          </>
        ) : searchTerm ? (
          <p className="text-center text-content-300 py-5">
            No results found. Try adjusting your search
          </p>
        ) : null}
      </div>
    </Modal>
  );
};
